/**
 * Overwatch report presenter (ENG-1758): the ONE place that maps an enhanced report's
 * status to a label key, tone, icon, progress steps, deadlines and "next step" copy.
 * Used by StatusBadge, TicketCard, TicketsTableComponent, the workspace header,
 * ReportProgress, NextStepCard and the staff review screen so they never disagree.
 *
 * Pure functions only (no Vue, no `@/` imports) so it is unit-tested from server/__tests__.
 */
import { ReportStatus } from "../../shared/ReportStatus";
import { TicketStatus } from "../../shared/TicketStatus";
import { ReportType } from "../../shared/ReportType";
import { ReportResolveType, TicketType } from "../../shared/Ticket";
import type { Ticket } from "../../shared/Ticket";

export type ReportTone = "secondary" | "warning" | "info" | "primary" | "danger" | "success" | "dark";
export type ReportRole = "reporter" | "accused" | "juror" | "staff" | "other";

/** i18n suffix under `reportStatus.*` for each status (canonical namespace). */
const STATUS_KEYS: Record<ReportStatus, string> = {
  [ReportStatus.CREATED]: "created",
  [ReportStatus.PENDING_RESPONSE]: "pending_response",
  [ReportStatus.ACCEPTED]: "accepted",
  [ReportStatus.DISPUTED]: "disputed",
  [ReportStatus.CONCILIATION]: "conciliation",
  [ReportStatus.AGREEMENT_REACHED]: "agreement_reached",
  [ReportStatus.CONCILIATION_EXPIRED]: "conciliation_expired",
  [ReportStatus.JURY_PHASE]: "jury_phase",
  [ReportStatus.JURY_VOTING]: "jury_voting",
  [ReportStatus.JURY_VALID]: "jury_valid",
  [ReportStatus.JURY_INVALID]: "jury_invalid",
  [ReportStatus.STAFF_REVIEW]: "staff_review",
  [ReportStatus.CLOSED]: "closed",
  [ReportStatus.STAFF_VOTING]: "staff_voting",
};

const STATUS_TONES: Record<ReportStatus, ReportTone> = {
  [ReportStatus.CREATED]: "secondary",
  [ReportStatus.PENDING_RESPONSE]: "warning",
  [ReportStatus.ACCEPTED]: "info",
  [ReportStatus.DISPUTED]: "danger",
  [ReportStatus.CONCILIATION]: "info",
  [ReportStatus.AGREEMENT_REACHED]: "success",
  [ReportStatus.CONCILIATION_EXPIRED]: "warning",
  [ReportStatus.JURY_PHASE]: "info",
  [ReportStatus.JURY_VOTING]: "info",
  [ReportStatus.JURY_VALID]: "success",
  [ReportStatus.JURY_INVALID]: "danger",
  [ReportStatus.STAFF_REVIEW]: "warning",
  [ReportStatus.CLOSED]: "dark",
  [ReportStatus.STAFF_VOTING]: "primary",
};

const STATUS_ICONS: Record<ReportStatus, string> = {
  [ReportStatus.CREATED]: "fa fa-file-circle-plus",
  [ReportStatus.PENDING_RESPONSE]: "fa fa-clock",
  [ReportStatus.ACCEPTED]: "fa fa-check",
  [ReportStatus.DISPUTED]: "fa fa-exclamation-triangle",
  [ReportStatus.CONCILIATION]: "fa fa-handshake",
  [ReportStatus.AGREEMENT_REACHED]: "fa fa-check-circle",
  [ReportStatus.CONCILIATION_EXPIRED]: "fa fa-hourglass-end",
  [ReportStatus.JURY_PHASE]: "fa fa-gavel",
  [ReportStatus.JURY_VOTING]: "fa fa-vote-yea",
  [ReportStatus.JURY_VALID]: "fa fa-check-double",
  [ReportStatus.JURY_INVALID]: "fa fa-times-circle",
  [ReportStatus.STAFF_REVIEW]: "fa fa-user-shield",
  [ReportStatus.CLOSED]: "fa fa-lock",
  [ReportStatus.STAFF_VOTING]: "fa fa-users-gear",
};

/** Bootstrap badge classes per tone (dark theme: warning/info need dark text). */
export const TONE_BADGE_CLASS: Record<ReportTone, string> = {
  secondary: "bg-secondary",
  warning: "bg-warning text-dark",
  info: "bg-info text-dark",
  primary: "bg-primary",
  danger: "bg-danger",
  success: "bg-success",
  dark: "bg-dark border border-secondary",
};

/** Hex colours per tone (workspace header status icon). */
export const TONE_COLOR: Record<ReportTone, string> = {
  secondary: "#6c757d",
  warning: "#ffc107",
  info: "#17a2b8",
  primary: "#ff3377",
  danger: "#dc3545",
  success: "#28a745",
  dark: "#adb5bd",
};

type StatusSource = Pick<Ticket, "reportStatus" | "juryNoVerdict">;

function isNoVerdict(t: Partial<StatusSource>): boolean {
  return t.reportStatus === ReportStatus.JURY_INVALID && !!t.juryNoVerdict;
}

/** Status key incl. the virtual `jury_no_verdict` (JURY_INVALID + juryNoVerdict). */
export function reportStatusKey(t: Partial<StatusSource>): string {
  if (t.reportStatus === undefined || t.reportStatus === null) return "unknown";
  if (isNoVerdict(t)) return "jury_no_verdict";
  return STATUS_KEYS[t.reportStatus as ReportStatus] ?? "unknown";
}

/** i18n key for the status label (`reportStatus.*`, falls back to `common.unknown`). */
export function reportStatusLabelKey(t: Partial<StatusSource>): string {
  const key = reportStatusKey(t);
  return key === "unknown" ? "common.unknown" : `reportStatus.${key}`;
}

export function reportStatusTone(t: Partial<StatusSource>): ReportTone {
  if (t.reportStatus === undefined || t.reportStatus === null) return "secondary";
  if (isNoVerdict(t)) return "secondary";
  return STATUS_TONES[t.reportStatus as ReportStatus] ?? "secondary";
}

export function reportStatusIcon(t: Partial<StatusSource>): string {
  if (t.reportStatus === undefined || t.reportStatus === null) return "fa fa-circle";
  if (isNoVerdict(t)) return "fa fa-scale-balanced";
  return STATUS_ICONS[t.reportStatus as ReportStatus] ?? "fa fa-circle";
}

/** Blinking warning icon used for reports flagged in fraganti (overrides the status icon). */
export const IN_FRAGANTI_ICON = "fa fa-triangle-exclamation fa-fade";

/** Icon for the plain open/closed ticket status (the report phase icon is `reportStatusIcon`). */
export function ticketStatusIcon(status: TicketStatus | number | undefined, inFraganti = false): string {
  if (inFraganti) return IN_FRAGANTI_ICON;
  switch (status) {
    case TicketStatus.OPENED:
      return "fa fa-circle-dot";
    case TicketStatus.CLOSED:
      return "fa fa-circle-check";
    default:
      return "fa fa-circle";
  }
}

/** CSS class (TicketCard status dot) for the plain open/closed status. */
export function ticketStatusClass(status: TicketStatus | number | undefined, inFraganti = false): string {
  if (inFraganti) return "status-report-open";
  switch (status) {
    case TicketStatus.OPENED:
      return "status-open";
    case TicketStatus.CLOSED:
      return "status-closed";
    default:
      return "status-unknown";
  }
}

/** Bootstrap badge class for the plain open/closed ticket status (admin tickets table). */
export function ticketStatusBadgeClass(status: TicketStatus | number | undefined): string {
  switch (status) {
    case TicketStatus.OPENED:
      return "badge bg-success";
    case TicketStatus.CLOSED:
      return "badge bg-secondary";
    default:
      return "badge bg-info";
  }
}

export function reportStatusBadgeClass(t: Partial<StatusSource>): string {
  return TONE_BADGE_CLASS[reportStatusTone(t)];
}

export function isEnhancedReportLike(t: Partial<Ticket> | null | undefined): boolean {
  return !!t && t.type === TicketType.REPORT && t.reportStatus !== undefined && t.reportStatus !== null;
}

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

type RoleSource = Pick<Ticket, "createdBy" | "reportAccused" | "juryMembers">;

/** Viewer role for an enhanced report. Staff wins over party only when not a party. */
export function viewerRole(t: Partial<RoleSource>, userId: string | undefined, isStaff: boolean): ReportRole {
  if (userId) {
    if (t.reportAccused?._id === userId) return "accused";
    if (t.createdBy?._id === userId) return "reporter";
    if (t.juryMembers?.some((m) => m?._id === userId)) return "juror";
  }
  if (isStaff) return "staff";
  return "other";
}

// ---------------------------------------------------------------------------
// Deadlines
// ---------------------------------------------------------------------------

export type DeadlineKind =
  | "response"
  | "conciliation"
  | "proposal"
  | "jurySelection"
  | "juryVoting"
  | "postJury"
  | "staffVoting"
  | "agreementClose";

/** Whose action the deadline waits for. */
export type DeadlineOwner = "accused" | "parties" | "reporter" | "jurors" | "staff" | "system" | "proposalResponder";

export interface ReportDeadline {
  kind: DeadlineKind;
  at: number;
  owner: DeadlineOwner;
}

type DeadlineSource = Pick<
  Ticket,
  | "reportStatus"
  | "juryNoVerdict"
  | "responseDeadline"
  | "conciliationDeadline"
  | "proposalResponseDeadline"
  | "jurySelectionDeadline"
  | "juryVotingDeadline"
  | "postJuryChoice"
  | "postJuryChoiceDeadline"
  | "staffVotingDeadline"
  | "agreementCloseDeadline"
>;

/** Who may make the post-jury choice: accused after "guilty", reporter after "not guilty", both on "no verdict". */
export function postJuryChooser(t: Partial<DeadlineSource>): DeadlineOwner {
  if (t.juryNoVerdict) return "parties";
  return t.reportStatus === ReportStatus.JURY_VALID ? "accused" : "reporter";
}

/** The one timer that drives the current status (undefined when nobody is on the clock). */
export function activeDeadline(t: Partial<DeadlineSource>): ReportDeadline | undefined {
  const d = (kind: DeadlineKind, at: number | undefined | null, owner: DeadlineOwner) =>
    at ? { kind, at, owner } : undefined;
  switch (t.reportStatus) {
    case ReportStatus.PENDING_RESPONSE:
      return d("response", t.responseDeadline, "accused");
    case ReportStatus.CONCILIATION:
      return d("conciliation", t.conciliationDeadline, "parties");
    case ReportStatus.JURY_PHASE:
      return d("jurySelection", t.jurySelectionDeadline, "system");
    case ReportStatus.JURY_VOTING:
      return d("juryVoting", t.juryVotingDeadline, "jurors");
    case ReportStatus.JURY_VALID:
    case ReportStatus.JURY_INVALID:
      return t.postJuryChoice ? undefined : d("postJury", t.postJuryChoiceDeadline, postJuryChooser(t));
    case ReportStatus.STAFF_VOTING:
      return d("staffVoting", t.staffVotingDeadline, "staff");
    case ReportStatus.AGREEMENT_REACHED:
      return d("agreementClose", t.agreementCloseDeadline, "staff");
    default:
      return undefined;
  }
}

/** Every timer that currently matters (active phase timer + the per-proposal 48 h timer). */
export function listReportDeadlines(t: Partial<DeadlineSource>): ReportDeadline[] {
  const list: ReportDeadline[] = [];
  const main = activeDeadline(t);
  if (main) list.push(main);
  if (t.reportStatus === ReportStatus.CONCILIATION && t.proposalResponseDeadline) {
    list.push({ kind: "proposal", at: t.proposalResponseDeadline, owner: "proposalResponder" });
  }
  return list.sort((a, b) => a.at - b.at);
}

export type DeadlineUrgency = "ok" | "soon" | "urgent" | "expired";

export function deadlineUrgency(at: number, now: number = Date.now()): DeadlineUrgency {
  const diff = at - now;
  if (diff <= 0) return "expired";
  if (diff < 6 * 3600_000) return "urgent";
  if (diff < 24 * 3600_000) return "soon";
  return "ok";
}

export interface CountdownParts {
  /** i18n key under `report.countdown.*` */
  key: "expired" | "in_minutes" | "in_hours_minutes" | "in_days_hours";
  params: { d?: number; h?: number; m?: number };
}

/**
 * Countdown that never rounds down to whole days: < 1 h -> minutes, < 48 h -> hours + minutes,
 * otherwise days + hours (e.g. 47 h 10 min is "in 47 h 10 min", never "1 day").
 */
export function countdownParts(at: number, now: number = Date.now()): CountdownParts {
  const diff = at - now;
  if (diff <= 0) return { key: "expired", params: {} };
  const totalMinutes = Math.max(1, Math.floor(diff / 60_000));
  const totalHours = Math.floor(totalMinutes / 60);
  if (totalHours < 1) return { key: "in_minutes", params: { m: totalMinutes } };
  if (totalHours < 48) return { key: "in_hours_minutes", params: { h: totalHours, m: totalMinutes % 60 } };
  return { key: "in_days_hours", params: { d: Math.floor(totalHours / 24), h: totalHours % 24 } };
}

/** "30 sept, 18:00" for a BCP-47 locale (callers map es/en with toIntlLocale from utils/format). */
export function formatDeadlineAbsolute(at: number, intlLocale?: string): string {
  try {
    return new Date(at).toLocaleString(intlLocale, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return new Date(at).toLocaleString();
  }
}

// ---------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------

export type StepId = "filed" | "response" | "conciliation" | "jury" | "verdict" | "staff" | "closed";
export type StepState = "done" | "current" | "future" | "skipped";

export interface ProgressStep {
  id: StepId;
  state: StepState;
  /** Timer for the current step, if any */
  deadline?: ReportDeadline;
}

type ProgressSource = DeadlineSource &
  Pick<
    Ticket,
    | "reportType"
    | "resolveType"
    | "conciliationStarted"
    | "juryMembers"
    | "juryVoteCount"
    | "penaltyMultiplier"
    | "staffVotingDeadline"
    | "staffVotes"
    | "staffVoteThresholdReached"
  >;

const CURRENT_STEP: Record<ReportStatus, StepId> = {
  [ReportStatus.CREATED]: "filed",
  [ReportStatus.PENDING_RESPONSE]: "response",
  // ACCEPTED / AGREEMENT_REACHED are NOT finished: staff still close them
  [ReportStatus.ACCEPTED]: "staff",
  [ReportStatus.DISPUTED]: "conciliation",
  [ReportStatus.CONCILIATION]: "conciliation",
  [ReportStatus.AGREEMENT_REACHED]: "staff",
  [ReportStatus.CONCILIATION_EXPIRED]: "conciliation",
  [ReportStatus.JURY_PHASE]: "jury",
  [ReportStatus.JURY_VOTING]: "jury",
  [ReportStatus.JURY_VALID]: "verdict",
  [ReportStatus.JURY_INVALID]: "verdict",
  [ReportStatus.STAFF_REVIEW]: "staff",
  [ReportStatus.STAFF_VOTING]: "staff",
  [ReportStatus.CLOSED]: "closed",
};

/** True when the accused accepted the report (the ACCEPTED status is transient: it auto-moves to STAFF_REVIEW). */
export function wasAcceptedByAccused(t: Partial<ProgressSource>): boolean {
  if (t.reportStatus === ReportStatus.ACCEPTED) return true;
  if (t.resolveType === ReportResolveType.ACCEPT_GUILT) return true;
  return (
    (t.reportStatus === ReportStatus.STAFF_REVIEW || t.reportStatus === ReportStatus.CLOSED) &&
    t.penaltyMultiplier != null &&
    t.penaltyMultiplier > 0 &&
    t.penaltyMultiplier < 1 &&
    !t.conciliationStarted &&
    !t.postJuryChoice &&
    !(t.juryMembers && t.juryMembers.length > 0)
  );
}

function juryHappened(t: Partial<ProgressSource>): boolean {
  const votes = (t.juryVoteCount?.valid ?? 0) + (t.juryVoteCount?.invalid ?? 0);
  return !!(
    (t.juryMembers && t.juryMembers.length > 0) ||
    votes > 0 ||
    t.juryNoVerdict ||
    t.postJuryChoice ||
    t.juryVotingDeadline
  );
}

function verdictHappened(t: Partial<ProgressSource>): boolean {
  const votes = (t.juryVoteCount?.valid ?? 0) + (t.juryVoteCount?.invalid ?? 0);
  return !!(t.postJuryChoice || t.juryNoVerdict || t.postJuryChoiceDeadline || votes > 0);
}

/**
 * Full step list with past/current/future/skipped. NORMAL reports have no jury/verdict steps.
 * Skipped = a step the report went past without using (e.g. conciliation after Accept).
 */
export function buildReportProgress(t: Partial<ProgressSource>): ProgressStep[] {
  const status = t.reportStatus as ReportStatus | undefined;
  if (status === undefined || status === null) return [];
  const isJuryBased = t.reportType === ReportType.JURY_BASED || juryHappened(t);
  const ids: StepId[] = isJuryBased
    ? ["filed", "response", "conciliation", "jury", "verdict", "staff", "closed"]
    : ["filed", "response", "conciliation", "staff", "closed"];

  const current = CURRENT_STEP[status] ?? "filed";
  const currentIndex = ids.indexOf(current);
  const accepted = wasAcceptedByAccused(t);
  const conciliationUsed =
    !!t.conciliationStarted ||
    [ReportStatus.DISPUTED, ReportStatus.CONCILIATION, ReportStatus.AGREEMENT_REACHED, ReportStatus.CONCILIATION_EXPIRED].includes(status);
  const deadline = activeDeadline(t);

  return ids.map((id, index) => {
    if (status === ReportStatus.CLOSED) {
      // Everything before "closed" either happened or was skipped
      if (id === "closed") return { id, state: "done" as StepState };
    } else if (index === currentIndex) {
      return { id, state: "current" as StepState, deadline };
    } else if (index > currentIndex) {
      return { id, state: "future" as StepState };
    }
    // Past steps
    let skipped = false;
    if (id === "conciliation") skipped = accepted || !conciliationUsed;
    if (id === "jury") skipped = !juryHappened(t);
    if (id === "verdict") skipped = !verdictHappened(t);
    return { id, state: (skipped ? "skipped" : "done") as StepState };
  });
}

// ---------------------------------------------------------------------------
// Next step copy
// ---------------------------------------------------------------------------

/**
 * i18n base key for the NextStepCard: `report.next.<statusKey>`. Each has `any` plus optional
 * role overrides (`reporter` / `accused` / `juror` / `staff`), each with `now` and `can`.
 * Post-jury statuses split into `*_chooser` / `*_waiting` for the parties.
 */
export function nextStepStatusKey(t: Partial<ProgressSource>): string {
  const status = t.reportStatus;
  if (status === ReportStatus.STAFF_REVIEW && wasAcceptedByAccused(t)) return "accepted";
  if (status === ReportStatus.CLOSED) return "closed";
  return reportStatusKey(t);
}

/** Can this role make the post-jury choice right now? */
export function canMakePostJuryChoice(t: Partial<DeadlineSource>, role: ReportRole): boolean {
  if (t.postJuryChoice) return false;
  if (t.reportStatus !== ReportStatus.JURY_VALID && t.reportStatus !== ReportStatus.JURY_INVALID) return false;
  const chooser = postJuryChooser(t);
  if (chooser === "parties") return role === "reporter" || role === "accused";
  return chooser === role;
}

/** Candidate i18n keys, most specific first: `report.next.<status>.<role>` then `.any`. */
export function nextStepKeys(t: Partial<ProgressSource>, role: ReportRole): string[] {
  const statusKey = nextStepStatusKey(t);
  const base = `report.next.${statusKey}`;
  const keys: string[] = [];
  if (statusKey === "jury_valid" || statusKey === "jury_invalid" || statusKey === "jury_no_verdict") {
    if (role === "reporter" || role === "accused") {
      keys.push(`${base}.${canMakePostJuryChoice(t, role) ? "chooser" : "waiting"}`);
    }
  }
  keys.push(`${base}.${role}`, `${base}.any`);
  return keys;
}

// ---------------------------------------------------------------------------
// Consequence previews
// ---------------------------------------------------------------------------

export interface ClosePenaltyPreview {
  kind: "none" | "blocks" | "scaled";
  count: number;
  multiplier: number;
}

/**
 * Mirrors the close logic in server/reportEndpoints.ts (closeReportCore): INVALID or a
 * majority-reject staff vote applies nothing; a post-jury multiplier (or the 1.5 appeal
 * multiplier) scales quantities at close. The accept reduction is already baked into the
 * proposed blocks when the accused accepts, so it is not re-applied here.
 */
export function closePenaltyPreview(
  t: Partial<Pick<Ticket, "penaltyMultiplier" | "postJuryChoice" | "staffVoteThresholdReached" | "staffVoteCount">>,
  resolveType: ReportResolveType | number,
  blockCount: number
): ClosePenaltyPreview {
  const none = { kind: "none" as const, count: 0, multiplier: 1 };
  if (resolveType === ReportResolveType.INVALID || blockCount <= 0) return none;
  if (t.staffVoteThresholdReached && t.staffVoteCount && t.staffVoteCount.reject > t.staffVoteCount.accept) {
    return none;
  }
  const m = t.penaltyMultiplier;
  if (m != null && m > 0 && m !== 1 && (t.postJuryChoice || m === 1.5)) {
    return { kind: "scaled", count: blockCount, multiplier: m };
  }
  return { kind: "blocks", count: blockCount, multiplier: 1 };
}

/** Multiplier applied if staff convict after the given post-jury choice (mirrors applyPostJuryChoice). */
export function appealMultiplier(t: Partial<Pick<Ticket, "juryNoVerdict">>): number {
  return t.juryNoVerdict ? 1 : 1.5;
}

// ---------------------------------------------------------------------------
// Jury join refusals
// ---------------------------------------------------------------------------

const JURY_JOIN_REASON_KEYS: Record<string, string> = {
  not_recruiting: "jury.join.not_recruiting",
  jury_full: "jury.join.jury_full",
  already_member: "jury.join.already_member",
  training_required: "jury.join.training_required",
  reputation_too_low: "jury.join.reputation_too_low",
  staff_excluded: "jury.join.staff_excluded",
  max_active_duties: "jury.join.max_active_duties",
  reporter: "jury.join.cannot_join_reporter",
  reported: "jury.join.cannot_join_reported",
  assigned_staff: "jury.join.cannot_join_staff",
  recently_served: "jury.join.recently_served",
  not_eligible_case: "jury.join.not_eligible_case",
  seat_taken: "jury.join.seat_taken",
};

/** i18n key for a refused jury join (`reason` code from POST /jury/join), generic when unknown. */
export function juryJoinDeniedMessageKey(reason: unknown): string {
  return (typeof reason === "string" && JURY_JOIN_REASON_KEYS[reason]) || "jury.join.cannot_join";
}
